// Shared Special Pricing (Vendor Bid) field logic.
//
// Both the create-template wizard (pages/Templates.jsx) and the Fields tab
// (pages/TemplateDetail.jsx) turn the same /netsuite/extract-from-document
// response into template fields. They used to do it with separate code, and
// only TemplateDetail knew this is a bid: the wizard mapped every row to a
// flat text field, so the eight line-table columns became bogus header
// fields, the product table got no field at all, and the labels the backend
// marks as not-a-field came through as fields to delete by hand.
//
// One helper, used by both, is what stops those two screens producing
// different templates from the same PDF.

// Line-record keys a Samsung bid prints ONCE in its header block. They are
// stored per line in NetSuite, but must stay HEADER template fields — the
// review screen is what copies them down onto every line, and it can only do
// that if something extracted them in the first place.
export const BID_HEADER_PRINTED_LINE_KEYS = new Set([
  'custrecord_jcs_spil_pricing_id',
  'custrecord_jcs_spil_valid_period_from',
  'custrecord_jcs_spil_valid_period_to',
  'custrecord_jcs_spil_reseller',
  'custrecord_jcs_spil_reseller_postal_add',
])

// The line-item table. Discount %, Line Amount and Pricing ID are deliberately
// absent: they are always computed from list price, bid price and quantity
// before posting, so a column for them would be a box that lies about being
// editable.
export const BID_LINE_ITEM_COLUMNS = [
  { name: 'item_no', type: 'number', description: "The line's own Item No / sequence number (e.g. 210, 340) — not the product code" },
  { name: 'item_code', type: 'text', description: 'Item/product code as it appears on the document' },
  { name: 'description', type: 'text', description: 'Item description' },
  { name: 'quantity', type: 'number', description: 'Quantity this special price applies to' },
  { name: 'list_price', type: 'currency', description: 'Standard/list price before the bid discount' },
  { name: 'bid_price', type: 'currency', description: 'The special/discounted price being bid' },
  { name: 'second_requested_price', type: 'currency', description: '2nd Requested Price (price limit) when the document prints one' },
  { name: 'valid_from', type: 'date', description: "Date this line's special pricing becomes valid" },
  { name: 'valid_to', type: 'date', description: "Date this line's special pricing expires" },
]

export const BID_LINE_ITEMS_FIELD = {
  field_name: 'line_items',
  display_name: 'Bid Line Items',
  field_type: 'array_of_objects',
  category: 'Bid Line Items',
  description:
    'Per-item special pricing (item code, quantity, list price, bid price, valid period). ' +
    'Discount %, Line Amount and Pricing ID are always computed by NetSuite/the app — never extracted from the document.',
  columns: BID_LINE_ITEM_COLUMNS,
}

/** A mapping the backend pinned to the known Samsung layout rather than guessed. */
export function isKnownLayoutMapping(m) {
  return (
    m?.mapping_source === 'known_layout' ||
    m?.mapping_source === 'known_layout_recovered'
  )
}

/**
 * Which mappings become template fields.
 *
 * Dropped, and why:
 *  - `exclude_from_template`: the backend recognised the label as not a
 *    Special Pricing field at all (Distributor, Total Price, the Competitor
 *    Info block, Samsung's own sales admin).
 *  - line-table columns: they are represented by the single line_items table,
 *    not by one header field each — except the keys a bid prints once in its
 *    header (BID_HEADER_PRINTED_LINE_KEYS).
 */
export function isBidHeaderMapping(m) {
  if (!m || m.exclude_from_template) return false
  if (m.is_line && !BID_HEADER_PRINTED_LINE_KEYS.has(m.suggested_ns_key)) return false
  return true
}

function fieldNameFor(m, index) {
  return (
    m.suggested_ns_key ||
    m.label?.toLowerCase().replace(/[^a-z0-9]+/g, '_') ||
    `field_${index + 1}`
  )
}

/**
 * Turn an extract-from-document response's mappings into Special Pricing
 * template fields: the header fields, plus the line-items table.
 *
 * @param {Array} mappings         `data.mappings` from the endpoint
 * @param {object} [options]
 * @param {boolean} [options.hasLineItems] the template already has a
 *        line_items field, so don't add a second one
 * @returns {Array} template field descriptors
 */
export function bidFieldsFromMappings(mappings, { hasLineItems = false } = {}) {
  const seenKeys = new Set()
  const fields = []

  ;(mappings || []).forEach((m, index) => {
    if (!isBidHeaderMapping(m)) return
    const key = fieldNameFor(m, index)
    if (seenKeys.has(key)) return
    seenKeys.add(key)
    fields.push({
      field_name: key,
      display_name: m.label,
      field_type: 'text',
      category: 'Bid Details',
      sample_value: m.sample_value,
      confidence: m.confidence,
      ns_key: m.suggested_ns_key || null,
      mapping_source: m.mapping_source || null,
      reason: typeof m.reason === 'string' ? m.reason : '',
      description: m.suggested_ns_key
        ? `Maps to NetSuite Special Pricing field: ${m.suggested_ns_key}. Appears on the document as "${m.label}".`
        : `No NetSuite mapping found. Appears on the document as "${m.label}".`,
    })
  })

  if (!hasLineItems) fields.push({ ...BID_LINE_ITEMS_FIELD })
  return fields
}

/**
 * The labels the backend read but deliberately left unmapped, so the review
 * screen can show they were seen rather than silently dropping them.
 */
export function excludedBidLabels(mappings) {
  return (mappings || [])
    .filter((m) => m?.exclude_from_template)
    .map((m) => ({ label: m.label, reason: typeof m.reason === 'string' ? m.reason : '' }))
}

// Standard Special Pricing header fields, for seeding a template without a
// sample document. Keys and mandatory status are the ones confirmed live
// against the sandbox (detect_active_bid_fields). Discount % and Line Amount
// are deliberately absent for the same reason they are absent from the line
// columns: they are always server-computed.
export const STANDARD_NS_BID_FIELDS = [
  { field_name: 'custrecord_project_name', display_name: 'Project Name', ns_key: 'custrecord_project_name' },
  { field_name: 'custrecord_jcs_sp_bids', display_name: 'Bid Number', ns_key: 'custrecord_jcs_sp_bids' },
  { field_name: 'custrecord_jcs_sp_supplier', display_name: 'Supplier', ns_key: 'custrecord_jcs_sp_supplier' },
  { field_name: 'custrecord_jcs_sp_reseller', display_name: 'Reseller', ns_key: 'custrecord_jcs_sp_reseller' },
  { field_name: 'custrecord_jcs_sp_end_customer', display_name: 'End Customer', ns_key: 'custrecord_jcs_sp_end_customer' },
  // The "Postal address" printed under End Customer — the end customer's own
  // address, not the distributor's ("Distributor Postal Address" is Midwich).
  { field_name: 'custrecord_jcs_sp_postal_address', display_name: 'Postal Address', ns_key: 'custrecord_jcs_sp_postal_address' },
  { field_name: 'custrecord_jcs_sp_expected_closed_date', display_name: 'Expected Close Date', ns_key: 'custrecord_jcs_sp_expected_closed_date' },
  // Printed once in a Samsung bid's header, stored per line in NetSuite —
  // the review screen copies them onto every line.
  { field_name: 'custrecord_jcs_spil_pricing_id', display_name: 'Pricing ID', ns_key: 'custrecord_jcs_spil_pricing_id' },
  { field_name: 'custrecord_jcs_spil_valid_period_from', display_name: 'Valid Period (From)', ns_key: 'custrecord_jcs_spil_valid_period_from' },
  { field_name: 'custrecord_jcs_spil_valid_period_to', display_name: 'Valid Period (To)', ns_key: 'custrecord_jcs_spil_valid_period_to' },
  { field_name: 'custrecord_jcs_spil_reseller_postal_add', display_name: 'Reseller Postal Address', ns_key: 'custrecord_jcs_spil_reseller_postal_add' },
]
  .map((f) => ({
    ...f,
    field_type: 'text',
    category: 'Bid Details',
    description: `Maps to NetSuite Special Pricing field: ${f.ns_key} (appears on the document as "${f.display_name}").`,
  }))
  .concat([{ ...BID_LINE_ITEMS_FIELD }])
